import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';

const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const AUTO_HINTS = {
  company_name: ['company', 'companyname', 'organization', 'org', 'business'],
  primary_contact: ['contact', 'contactname', 'name', 'primarycontact', 'fullname'],
  title: ['title', 'jobtitle', 'position', 'role'],
  email: ['email', 'emailaddress'],
  phone: ['phone', 'otherphone', 'phonenumber'],
  office_phone: ['officephone', 'workphone'],
  mobile_phone: ['mobilephone', 'mobile', 'cell', 'cellphone'],
  website: ['website', 'url', 'site'],
  industry: ['industry', 'sector'],
  contact_type: ['contacttype', 'type', 'audiencetype'],
  lead_source: ['leadsource', 'source'],
  campaign: ['campaign'],
  address_line1: ['address', 'officeaddress', 'street', 'addressline1', 'streetaddress1'],
  address_line2: ['addressline2', 'address2', 'streetaddress2', 'suite', 'unit'],
  address_city: ['city', 'officecity'],
  address_state: ['state', 'officestate'],
  address_zip: ['zip', 'zipcode', 'postalcode', 'officezip'],
  address_county: ['county'],
  address_country: ['country'],
  mailing_address_line1: ['mailingaddress', 'pobox', 'mailingaddresspobox'],
  mailing_city: ['mailingcity'],
  mailing_state: ['mailingstate'],
  mailing_zip: ['mailingzip'],
  partnership_type: ['partnershiptype', 'type'],
  partnership_tier: ['partnershiptier', 'tier'],
  status: ['status', 'stage', 'pipelinestage'],
  estimated_revenue: ['estimatedvalue', 'estimatedrevenue', 'value', 'opportunityvalue'],
  revenue: ['revenue', 'closedrevenue'],
  notes: ['notes', 'comments', 'description'],
  tags: ['tags', 'labels'],
};

export default function ImportLeadsDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState('upload'); // upload | map | result
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const reset = () => { setStep('upload'); setSession(null); setMapping({}); setResult(null); };

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/imports/leads/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSession(r.data);
      const auto = {};
      r.data.target_fields.forEach(({ key }) => {
        const hints = AUTO_HINTS[key] || [normalize(key)];
        const match = r.data.columns.find((c) => hints.includes(normalize(c)));
        if (match) auto[key] = match;
      });
      setMapping(auto);
      setStep('map');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read file');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!mapping.company_name) return toast.error('Map the Company Name field first');
    setBusy(true);
    try {
      const r = await api.post('/imports/leads/commit', { session_id: session.session_id, mapping });
      setResult(r.data);
      setStep('result');
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid="import-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-magenta" /> Import CRM Records
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file with a header row. You'll map columns to CRM fields on the next screen. Max 2,000 rows.
            </p>
            <label className="border-2 border-dashed border-slate-200 hover:border-magenta/50 rounded-xl p-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition" data-testid="import-dropzone">
              <FileSpreadsheet className="w-10 h-10 text-slate-300" />
              <div className="text-sm font-medium text-navy">{busy ? 'Reading file…' : 'Click to choose a CSV or Excel file'}</div>
              <input
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                disabled={busy}
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="import-file-input"
              />
            </label>
          </div>
        )}

        {step === 'map' && session && (
          <>
            <div className="text-sm text-slate-500 mb-1">
              <strong className="text-navy">{session.total_rows}</strong> rows found in <strong className="text-navy">{session.columns.length}</strong> columns. Map your file's columns to CRM fields:
            </div>
            <div className="max-h-[50vh] overflow-y-auto pr-2 grid grid-cols-2 gap-x-4 gap-y-2" data-testid="import-mapping-grid">
              {session.target_fields.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <Label className={`text-xs ${key === 'company_name' ? 'font-bold text-magenta' : ''}`}>{label}</Label>
                  <Select
                    value={mapping[key] || NONE}
                    onValueChange={(v) => setMapping({ ...mapping, [key]: v === NONE ? '' : v })}
                  >
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid={`map-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— skip —</SelectItem>
                      {session.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {session.preview?.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 overflow-x-auto">
                <div className="font-semibold text-slate-600 mb-1">Preview (first row):</div>
                {session.columns.slice(0, 6).map((c) => (
                  <span key={c} className="inline-block mr-3"><strong>{c}:</strong> {session.preview[0][c] || '—'}</span>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button onClick={commit} disabled={busy} className="bg-magenta hover:bg-magenta/90" data-testid="import-commit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Import {session.total_rows} rows
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'result' && result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
              <div>
                <div className="font-semibold text-navy">{result.created} partner{result.created === 1 ? '' : 's'} imported</div>
                <div className="text-xs text-slate-500">
                  {result.skipped_duplicates} duplicate{result.skipped_duplicates === 1 ? '' : 's'} skipped · {result.total_rows} rows processed
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-h-40 overflow-y-auto">
                <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Warnings</div>
                <ul className="text-xs text-amber-700 space-y-1">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => { onOpenChange(false); reset(); }} className="bg-magenta hover:bg-magenta/90" data-testid="import-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
